/**
 * Универсальная форма добавления нового элемента. Минимум заполняется URI элемента и его название
 * 
 * Если использовать в модельном окне не забыть ставить destroyOnHidden, если форма каждый раз должна открываться «с нуля».
 * 
 * В форму добавлено reserve={false} на Form, чтобы значения полей не сохранялись после закрытия.
 */
import React from 'react';
import { Form, Input, Select, Row, Col, Button } from 'antd';
import Icon from '@ant-design/icons';
import Label from '../widgets/Label';
import { makeURI } from '../../utils/uriUtil';

const { Option } = Select;

const formItemLayout = {
  labelCol  : { xs: { span: 24 }, sm: { span: 6 }, },
  wrapperCol: { xs: { span: 24 }, sm: { span: 18 }, },
};

const tailLayout = {
  wrapperCol: {
    offset: 8,
    span: 16,
  },
};


const FieldProxy = ({factory, predicat, ...props}) =>{
  const Component = factory.ui.getFormItem(predicat);
  const value = "";
  const lang = "";
  return (
    <Component lang={lang} uri={predicat} value={value} factory={factory} {...props} />
  )
}

/** Компонента, которая обеспечивает одновременный ввод наименование и языка */
class LabelInput extends React.Component {

  handleLabelChange = e => {
    this.triggerChange({text: e.target.value });
  };

  handleLangChange = lang => {
    this.triggerChange({ lang });
  };

  triggerChange = changedValue => {
    const { onChange, value } = this.props;
    if (onChange) {
      onChange({
        ...value,
        ...changedValue,
      });
    }
  };

  render() {
    const { value } = this.props;
    return (
      <Row gutter={8}>
        <Col span={20}>
          <Input prefix={<Icon type="edit" style={{ color: 'rgba(0,0,0,.25)' }} />} type="text" value={value.text} onChange={this.handleLabelChange} placeholder="Наименование"/>
        </Col>
        <Col span={4}>
          <Select
            value={value.lang}
              onChange={this.handleLangChange}>
            <Option value=""></Option>
            <Option value="ru">ru</Option>
            <Option value="en">en</Option>
          </Select>
        </Col>
      </Row>
    );
  }
}

/** Форма добавления нового элемента
 * Обязательно указать IRI.
 * Для указания наименования необходимо определить предикат. 
 * 
 * Вызов создания нового элемента см. AssetList.js
 *   <NewElement wrappedComponentRef={formRef} rdfType={"http://www.w3.org/ns/dcat#Dataset"}/>
 * 
 * Возможность добавления дополнительных полей.
 *   1. extra - список дополнительныйх поей
 *   2. tail - дополнительные ссылки, например для заполнения данными, переход на справку и т.п.
 *  
 */
class NewAdmsElementForm extends React.Component {

  state = {
    EP:"2026",
    iri: "http://www.w3.org/ns/dcat#",
    prefixIRI: "http://www.w3.org/ns/dcat#",
    defaultRdfType: "http://www.w3.org/ns/dcat#Dataset",
    prevPrefixURI: null
  };

  static getDerivedStateFromProps(props, state) {
    const  { prefixURI } = props;
    if (prefixURI && prefixURI !== state.prevPrefixURI)
      return {
        iri: prefixURI,
        prefixIRI: prefixURI,
        prevPrefixURI: prefixURI
      };    
    return null;
  };

  componentDidMount() {
    const { rdfTypes } = this.props;

    if (Array.isArray(rdfTypes) && rdfTypes.length > 1) {
      this.handleRdfTypeChange(rdfTypes[0]);
    }
  };

  handleIRIChange = e => {
    const value = e.target.value;
    const iri = (value && (value.startsWith("http://") || value.startsWith("https://"))) 
      ? value 
      : makeURI(this.state.prefixIRI,e.target.value);
    this.setState({iri});
    this.props.formRef.current.setFieldsValue({"IRI": e.target.value});
  };

  /** При изменении выбора rdf.type меняется префикс для формирования URI */
  handleRdfTypeChange = selectedType => {
    const { onMakePrefixURI, formRef } = this.props;

    if (typeof onMakePrefixURI === "function") {
      const prefixIRI = onMakePrefixURI(selectedType);
      
      if (prefixIRI) {              
        const iriValue = formRef.current ? formRef.current.getFieldValue("IRI") : "";  
        const iri = (iriValue == undefined) 
          ? prefixIRI 
          : iriValue && (iriValue.startsWith("http://") || iriValue.startsWith("https://") )
            ? iriValue
            : makeURI(prefixIRI, iriValue);
        this.setState({prefixIRI, iri});
      }
    }
  };

  render() {
    const {factory, tail} = this.props;
    const extra = (Array.isArray(this.props.extra))?this.props.extra:[];
    const firstType = (Array.isArray(this.props.rdfTypes) && this.props.rdfTypes.length>0)?  this.props.rdfTypes[0]:this.state.defaultRdfType;
    const rdfType = this.props.rdfType ? this.props.rdfType : firstType;
    return (
        <Form ref={this.props.formRef} {...formItemLayout} className="ant-edit-form" reserve={false}>
          <Form.Item name="IRI" label="URI" extra={this.state.iri}             
            rules={[
              { required: true, message: 'Пожалуйста, укажите URI!' }
            ]}
          >
            <Input prefix={<Icon type="shrink" style={{ color: 'rgba(0,0,0,.25)' }} />} placeholder="URI" onChange={this.handleIRIChange}/>
          </Form.Item>
          <Form.Item label={<Label />} 
            name="http://www.w3.org/2000/01/rdf-schema#label"
            initialValue={{ text: "", lang: '' }}
            rules={[
              {
                validator: async (_, value) => {
                  if (!value || !value.text || value.text === 0 )
                    return Promise.reject(new Error('Пожалуйста, укажите наименование!'));
                },                
              },
              { required: true, message: 'Пожалуйста, укажите наименование!' }
            ]}
          >
            <LabelInput />
          </Form.Item>
          {Array.isArray(this.props.rdfTypes) && this.props.rdfTypes.length > 0 ? (
            <Form.Item
              label={<Label iri="http://www.w3.org/1999/02/22-rdf-syntax-ns#type" />}
              name="http://www.w3.org/1999/02/22-rdf-syntax-ns#type"
              initialValue={rdfType}
              rules={[{ required: true, message: 'Пожалуйста, выберите тип!' }]}
            >
              <Select placeholder="Выберите тип элемента" onChange={this.handleRdfTypeChange}>
                {this.props.rdfTypes.map((type, index) => (
                  <Option key={index} value={type}>
                    {type}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <Form.Item
              label={<Label iri="http://www.w3.org/1999/02/22-rdf-syntax-ns#type" />}
              name="http://www.w3.org/1999/02/22-rdf-syntax-ns#type"
              initialValue={rdfType}
              rules={[{ required: true, message: 'Пожалуйста, выберите тип!' }]}
            >
              <Input
                prefix={<Icon type="user" style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder="Тип элемента"
                disabled
              />
            </Form.Item>
          )}  
          {extra.map((item, i) =>
            <FieldProxy key={i} factory={factory} predicat={item.uri} {...item}/>
          )}
          {Array.isArray(tail) &&
            <Form.Item {...tailLayout}>
              {tail.map((item, i) =>
                <Button key={i} {...item}>{item.text}</Button>
              )}
            </Form.Item>
          }
        </Form>
    );
  }
}

export default NewAdmsElementForm;